import React, { ChangeEvent } from "react";
import Input from "common/Inputs/Input.tsx";
import styled, { css, useTheme } from "styled-components";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import { NEW_LOOK_PAGE_GUTTER_VAR } from "common/NewLook";
import Panel from "common/NewLook/Panel.tsx";
import Skeleton from "common/NewLook/Skeleton.tsx";
import Flex from "common/Boxes/Flex.tsx";
import Title from "common/Typography/Title.tsx";
import HighlightedText from "common/Text/HighlightedText.tsx";
import { FileImageOutlined } from "@ant-design/icons";
import Link from "common/Typography/Link.tsx";
import { showIntercomSpace } from "utils/misc.tsx";
import {
  MOBILE_SEARCH,
  PAGE_SPINNER_MARGIN_FROM_TOP,
  TABLET,
} from "common/constants.ts";
import useBusinessProfile from "features/assetManager/BusinessProfile/useBusinessProfile.ts";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { AnalyticsPageName, LanguageNameSpaces } from "@shared/types";
import RequiredFieldMessage from "common/Animations/RequiredFieldMessage.tsx";
import { INPUT_MAX_LENGTH } from "features/assetManager/BusinessProfile/constants.ts";
import { HelperTooltip } from "common/Tooltips/HelperTooltip.tsx";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import Button from "common/Buttons/Button.tsx";
import ExchangesSelector from "features/assetManager/BusinessProfile/ExchangesSelector.tsx";
import ColorSelectors from "features/assetManager/BusinessProfile/ColorSelectors.tsx";
import { useBranding } from "@shared/utils/hooks/useBranding.ts";
import Select from "common/Select/Select.tsx";
import { LANGUAGE_OPTIONS } from "utils/language.ts";

const BusinessProfile = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ASSET_MANAGER);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const {
    onSubmit,
    isLoading,
    allExchanges,
    allSelected,
    tabTitle,
    setTabTitle,
    displayName,
    setDisplayName,
    language,
    setLanguage,
    lightModePrimaryColor,
    setLightModePrimaryColor,
    darkModePrimaryColor,
    setDarkModePrimaryColor,
    supportedExchanges,
    setSupportedExchanges,
    setConfigurationLoading,
    disabled,
    tabTitleError,
    displayNameError,
    supportedExchangesError,
    includeNewExchanges,
    setIncludeNewExchanges,
    allSupportedExchanges,
  } = useBusinessProfile();
  const { isWhitelabel } = useBranding();
  const { isNewLook } = useTheme();

  const submitButton = (
    <SubmitButton
      type="primary"
      onClick={onSubmit}
      loading={setConfigurationLoading}
      disabled={disabled}
    >
      {t("configuration.buttons.submit")}
    </SubmitButton>
  );

  const imagesInfo = (
    <span>
      {t("configuration.messages.info.images_description")}
      <StyledLink onClick={() => showIntercomSpace("help")}>
        {t("configuration.messages.info.contact_support")}
      </StyledLink>
    </span>
  );

  const fields = (
    <>
      <Item $half={true}>
        <ItemTitle>
          {t("configuration.titles.tab_title")}
          <HelperTooltip
            analyticsPageName={AnalyticsPageName.ASSET_MANAGER}
            analyticsFieldName={t("configuration.titles.tab_title")}
            title={t("configuration.titles.tab_title")}
            description={t("configuration.tooltips.tab_title_description")}
          />
        </ItemTitle>
        <Input
          value={tabTitle}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setTabTitle(e.target.value)
          }
          placeholder={t("configuration.placeholders.tab_title")}
        />
        <RequiredFieldMessage
          requirement={tabTitleError}
          errorMessage={t("configuration.messages.errors.max_length", {
            item: "Tab title",
            length: INPUT_MAX_LENGTH,
          })}
          style={ERROR_MESSAGE_STYLE}
        />
      </Item>
      <Item $half={true}>
        <ItemTitle>
          {t("configuration.titles.display_name")}
          <HelperTooltip
            analyticsPageName={AnalyticsPageName.ASSET_MANAGER}
            analyticsFieldName={t("configuration.titles.display_name")}
            title={t("configuration.titles.display_name")}
            description={t("configuration.tooltips.display_name_description")}
          />
        </ItemTitle>
        <Input
          value={displayName}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setDisplayName(e.target.value)
          }
          placeholder={t("configuration.placeholders.display_name")}
        />
        <RequiredFieldMessage
          requirement={displayNameError}
          errorMessage={t("configuration.messages.errors.max_length", {
            item: "Display name",
            length: INPUT_MAX_LENGTH,
          })}
          style={ERROR_MESSAGE_STYLE}
        />
      </Item>
      <ExchangesSelector
        allExchanges={allExchanges}
        supportedExchanges={supportedExchanges}
        setSupportedExchanges={setSupportedExchanges}
        supportedExchangesError={supportedExchangesError}
        includeNewExchanges={includeNewExchanges}
        setIncludeNewExchanges={setIncludeNewExchanges}
        allSelected={allSelected}
        allSupportedExchanges={allSupportedExchanges}
        isBottomNavigator={isBottomNavigator}
      />
      {isWhitelabel && (
        <ColorSelectors
          lightModePrimaryColor={lightModePrimaryColor}
          setLightModePrimaryColor={setLightModePrimaryColor}
          darkModePrimaryColor={darkModePrimaryColor}
          setDarkModePrimaryColor={setDarkModePrimaryColor}
        />
      )}
      <Item $half={true}>
        <ItemTitle>{t("configuration.titles.language")}</ItemTitle>
        <Select
          value={language}
          onChange={setLanguage}
          options={LANGUAGE_OPTIONS}
          placeholder={t("configuration.placeholders.language")}
          mobileTitle={t("configuration.titles.language")}
          style={{ width: "100%" }}
          showSearch={false}
        />
      </Item>
    </>
  );

  // New look: the form is a panel (labels above fields, Submit bottom-right,
  // full width on phones) and the note about images a quiet panel beside
  // it; panels shaped like the form while it loads.
  if (isNewLook)
    return (
      <Wrapper>
        <InnerWrapper>
          <Panel title={t("configuration.titles.profile_details")}>
            {isLoading ? (
              <FormGrid aria-busy={true}>
                {[0, 1, 2, 3].map((index) => (
                  <Item key={index} $half={index !== 2}>
                    <Skeleton width={90} height={12} />
                    <Skeleton
                      width="100%"
                      height={32}
                      radius={8}
                      style={{ marginTop: 8 }}
                    />
                  </Item>
                ))}
              </FormGrid>
            ) : (
              <FormGrid>
                {fields}
                <NewLookFormFooter>{submitButton}</NewLookFormFooter>
              </FormGrid>
            )}
          </Panel>
          <Panel title={t("configuration.titles.images")}>
            <NewLookInfo>{imagesInfo}</NewLookInfo>
          </Panel>
        </InnerWrapper>
      </Wrapper>
    );

  return (
    <LoadingSpinnerOverlay
      isLoading={isLoading}
      spinnerSize="large"
      spinnerMarginTop={PAGE_SPINNER_MARGIN_FROM_TOP}
      hideContentWhileLoading={true}
    >
      <Wrapper>
        <InnerWrapper>
          <LoadingSpinnerOverlay
            isLoading={setConfigurationLoading}
            wrap={true}
          >
            <ItemsWrapper>{fields}</ItemsWrapper>
          </LoadingSpinnerOverlay>
          <Summary>
            <HighlightedText hideLine={true}>
              <Flex $alignItems="flex-start">
                <ImageIcon />
                {imagesInfo}
              </Flex>
            </HighlightedText>
            {submitButton}
          </Summary>
        </InnerWrapper>
      </Wrapper>
    </LoadingSpinnerOverlay>
  );
};

export default BusinessProfile;

const ERROR_MESSAGE_STYLE = { marginTop: 4 };

const Wrapper = styled(Flex)`
  padding: 20px;
  align-items: flex-start;
  justify-content: center;
  @media (max-width: ${MOBILE_SEARCH}) {
    padding: 16px;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      padding: 16px ${NEW_LOOK_PAGE_GUTTER_VAR} 32px;
      justify-content: flex-start;
      @media (max-width: ${MOBILE_SEARCH}) {
        padding: 16px 16px 24px;
      }
    `}
`;
const InnerWrapper = styled(Flex)`
  max-width: 1175px;
  align-items: flex-start;
  column-gap: 24px;
  width: 100%;

  @media (max-width: ${TABLET}) {
    flex-direction: column;
    row-gap: 16px;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* The form leads; the note about images sits beside it (under it on
         narrow screens). */
      max-width: 1080px;
      display: grid;
      grid-template-columns: minmax(0, 1fr) 300px;
      gap: 16px;
      @media (max-width: ${TABLET}) {
        grid-template-columns: minmax(0, 1fr);
        gap: 12px;
      }
    `}
`;
const ItemsWrapper = styled(Flex)`
  flex-direction: column;
  align-items: flex-start;
  row-gap: 24px;
  border: ${(p) => p.theme.borders.medium};
  border-radius: 8px;
  background: ${(p) => p.theme.colors.cardBgColor};
  padding: 24px;
  width: 100%;
  @media (max-width: ${MOBILE_SEARCH}) {
    padding: 16px;
  }
`;
// New look: two columns of fields in the panel (one on phones); short
// fields take one column, the exchanges and colours the full width.
const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  align-items: start;
  > * {
    grid-column: 1 / -1;
    min-width: 0;
  }
  @media (max-width: ${MOBILE_SEARCH}) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
const Item = styled.div<{ $half?: boolean }>`
  width: 100%;
  ${(p) =>
    p.theme.isNewLook &&
    p.$half &&
    css`
      && {
        grid-column: auto / span 1;
      }
    `}
`;
const ItemTitle = styled(Title).attrs({ level: 4 })`
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  font-size: ${(p) => p.theme.typography.fontSizes.S};
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* Field labels in the label role. */
      && {
        margin-bottom: 6px;
        gap: 2px;
        ${newLookType.label};
        color: ${getNewLookTokens(p.theme).secondary};
      }
    `}
`;
const StyledLink = styled(Link)`
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  text-decoration: underline;
  margin-left: 4px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      && {
        font-size: inherit;
        font-weight: ${p.theme.typography.fontWeights.MEDIUM};
        text-underline-offset: 2px;
      }
    `}
`;
const Summary = styled.div`
  max-width: 400px;
  @media (max-width: ${TABLET}) {
    max-width: 100%;
  }
`;
const SubmitButton = styled(Button)`
  width: 100%;
  height: 38px;
  margin-top: 16px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      width: auto;
      min-width: 120px;
      height: 36px;
      margin-top: 0;
    `}
`;
const ImageIcon = styled(FileImageOutlined)`
  font-size: 32px;
  margin-right: 8px;
  top: 4px;
  position: relative;
`;
// The divider sits 20 below the last field and 16 above the button, which
// has the panel's 20 below it.
const NewLookFormFooter = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
  padding-top: 16px;
  border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  @media (max-width: ${MOBILE_SEARCH}) {
    margin-top: 0;
    padding-top: 16px;
    .ant-btn {
      width: 100%;
      height: 44px;
    }
  }
`;
const NewLookInfo = styled.p`
  margin: 0;
  ${newLookType.body};
  font-size: 13.5px;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
